import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Rating,
  IconButton,
  Chip,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddShoppingCart from '@mui/icons-material/AddShoppingCart';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Favorite from '@mui/icons-material/Favorite';
import Visibility from '@mui/icons-material/Visibility';
import LocalShipping from '@mui/icons-material/LocalShipping';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import TrendingIcon from '@mui/icons-material/Whatshot';
import ViewIcon from '@mui/icons-material/Visibility';
import { Product } from '../../data/mockMarketplace';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onToggleFavorite: (id: string | number) => void;
  isFavorite: boolean;
  onQuickView: (product: Product) => void;
}

const ProductCardEnhanced: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onToggleFavorite,
  isFavorite,
  onQuickView,
}) => {
  const navigate = useNavigate();
  const discount = product.originalPrice && product.price < product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <Card
      onClick={() => navigate(`/marketplace/product/${product.id}`)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          '& .hover-actions': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Badges */}
      <Stack direction="column" spacing={1} sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>
        {discount > 0 && (
          <Chip
            label={`-${discount}%`}
            color="error"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {product.isNew && (
          <Chip
            label="NEW"
            color="primary"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {product.isBestSeller && (
          <Chip
            label="BESTSELLER"
            color="warning"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {(product as any).isTrending && (
          <Chip
            icon={<TrendingIcon sx={{ '&&': { fontSize: 14, color: 'white' } }} />}
            label="TRENDING"
            color="error"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {(product as any).recentViews && (
          <Chip
            icon={<ViewIcon sx={{ '&&': { fontSize: 14, color: 'white' } }} />}
            label={`${(product as any).recentViews} views today`}
            sx={{ fontWeight: 'bold', bgcolor: 'rgba(0,0,0,0.6)', color: 'white' }}
            size="small"
          />
        )}
        {(product as any).campaign_name && (
          <Chip
            label={(product as any).campaign_name}
            color="secondary"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {/* Logistics Badges */}
        {product.description?.includes('[Logistics: Self-Transport') && (
          <Chip
            label="Self-Transport"
            color="info"
            size="small"
            icon={<LocalShipping sx={{ '&&': { fontSize: 14 } }} />}
            sx={{ fontWeight: 'bold', bgcolor: 'info.dark', color: 'white' }}
          />
        )}
        {product.description?.includes('[Logistics: Partner') && (
          <Chip
            label="Partner Delivery"
            color="secondary"
            size="small"
            icon={<LocalShipping sx={{ '&&': { fontSize: 14 } }} />}
            sx={{ fontWeight: 'bold' }}
          />
        )}
      </Stack>

      {/* Hover Actions */}
      <Box
        className="hover-actions"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          opacity: 0,
          transform: 'translateY(10px)',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Tooltip title={isFavorite ? "Remove from wishlist" : "Add to wishlist"} arrow placement="left">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(product.id); }}
            sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'white', color: 'primary.main' } }}
          >
            {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Quick View" arrow placement="left">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onQuickView(product); }}
            sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'white', color: 'primary.main' } }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      </Box>

      <CardMedia
        component="img"
        image={product.image}
        alt={product.name}
        sx={{ 
          height: 240, 
          width: '100%',
          objectFit: 'cover',
          display: 'block'
        }}
      />

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* Category & Seller */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {product.category}
          </Typography>
          {product.seller.verified && (
            <Tooltip title="Verified Seller">
              <VerifiedUser sx={{ fontSize: 16, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>

        <Typography gutterBottom variant="subtitle1" component="h2" sx={{ fontWeight: 'bold', lineHeight: 1.2, height: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {product.name}
        </Typography>

        {/* Rating & Seller */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Rating value={product.rating} readOnly size="small" precision={0.5} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({product.reviews})
            </Typography>
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={(e) => {
              e.stopPropagation();
              // If we have a shop_slug, go to shop profile, else go to seller profile
              if ((product as any).shop_slug) {
                navigate(`/shops/${(product as any).shop_slug}`);
              } else if ((product as any).shop_id) {
                // We'd need a slug, but for now fallback to seller
                navigate(`/seller/${product.seller.id}`);
              } else {
                navigate(`/seller/${product.seller.id}`);
              }
            }}
          >
            {product.seller.name}
          </Typography>
        </Box>

        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            KSh {product.price.toLocaleString()}
          </Typography>
          {product.originalPrice && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              KSh {product.originalPrice.toLocaleString()}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            / {product.unit}
          </Typography>
        </Box>

        <Button
          variant="contained"
          fullWidth
          startIcon={<AddShoppingCart />}
          onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Add to Cart
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCardEnhanced;
